import type { PendingUploadTarget } from '@/lib/uploads/upload-manager/background-upload-manager';

/**
 * The waiting send, once it exists; null when the send gave its uploads back
 * before any delivery took them (they belong to the composer again). A
 * rejection means the delivery tried and failed: the uploads are cancelled.
 */
type TargetOutcome = Promise<PendingUploadTarget | null>;
type Entry = {
  promise: TargetOutcome;
  resolve: (target: PendingUploadTarget | null) => void;
  reject: (error: unknown) => void;
  /** The delivery took it: only the delivery settles it from here on. */
  claimed: boolean;
};

/**
 * The waiting send each in-flight upload was handed to at the press (#4844),
 * keyed by the send's clientInputId. The composer creates it and adopts the
 * uploads with its promise; the navigation delivery claims it and resolves it
 * once the server has the send (or rejects it, which cancels the uploads). An
 * unclaimed one is given back instead, which returns the uploads to the
 * composer still running.
 * Module-level on purpose: it must outlive the composer that created it.
 */
const targets = new Map<string, Entry>();

export function createPendingUploadSendTarget(
  clientInputId: string
): TargetOutcome {
  // A repeated id keeps the first target: its uploads are already bound to it.
  const existing = targets.get(clientInputId);
  if (existing) return existing.promise;
  let resolve!: Entry['resolve'];
  let reject!: Entry['reject'];
  const promise = new Promise<PendingUploadTarget | null>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  // Each adopted upload handles the rejection; this keeps an unadopted one quiet.
  promise.catch(() => undefined);
  targets.set(clientInputId, { promise, resolve, reject, claimed: false });
  return promise;
}

export function hasPendingUploadSendTarget(clientInputId: string): boolean {
  return targets.has(clientInputId);
}

/** The delivery owns the target from here; the composer can no longer abandon it. */
export function claimPendingUploadSendTarget(clientInputId: string): boolean {
  const entry = targets.get(clientInputId);
  if (!entry) return false;
  entry.claimed = true;
  return true;
}

export function resolvePendingUploadSendTarget(
  clientInputId: string,
  target: PendingUploadTarget
): void {
  const entry = targets.get(clientInputId);
  if (!entry) return;
  targets.delete(clientInputId);
  entry.resolve(target);
}

export function rejectPendingUploadSendTarget(
  clientInputId: string,
  error: unknown
): void {
  const entry = targets.get(clientInputId);
  if (!entry) return;
  targets.delete(clientInputId);
  entry.reject(error);
}

/** No delivery took it: its uploads go back to the composer, still running. */
function giveBackPendingUploadSendTarget(clientInputId: string): void {
  const entry = targets.get(clientInputId);
  if (!entry || entry.claimed) return;
  targets.delete(clientInputId);
  entry.resolve(null);
}

/**
 * The composer's own send ended. A target no delivery claimed never will be
 * (validation stopped it, or the destination never became ready). Its
 * uploads return to the composer, as they would have run before the press,
 * instead of running for a send that does not exist or being cancelled.
 */
export function abandonPendingUploadSendTarget(clientInputId: string): void {
  giveBackPendingUploadSendTarget(clientInputId);
}

/**
 * The uploads a send takes along, if its delivery can take them: only a
 * send to one new task can wait on them. Any other send gives them back to
 * the composer (and fails at registration, which shows the retry panel)
 * rather than bind them to a waiting send that is never created.
 */
export function pendingUploadIdsForDelivery(
  clientInputId: string,
  uploadIds: ReadonlyArray<string> | undefined,
  oneNewTask: boolean
): string[] | undefined {
  if (!uploadIds?.length) return undefined;
  if (oneNewTask) return [...uploadIds];
  giveBackPendingUploadSendTarget(clientInputId);
  return undefined;
}
